import styled from 'styled-components';
import { breakpoint, font } from 'styles/globals';
import { blockQuotes, pullQuotes, longQuotes } from 'styles/quotes';
import { headers } from 'styles/headers';
import { images } from 'styles/images';
import { paragraphs, codeBlocks } from 'styles/paragraphs';
import { tables } from 'styles/tables';
import { videos } from 'styles/videos';
import { handleScrollJump, handleScrollJumpMobile } from 'styles/articleContent';
import Heading from 'styles/typography/Heading';
import { Theme, getBodyStyle, getColor } from 'styles/theme';

export const SponsoredSidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 300px !important;

  .most-popular-rr { 
    margin-bottom: 48px;
    margin-top: 24px;

    @media (${breakpoint.xlMin}) {
      margin-top: 0;
    }
  }

  & > div:nth-child(2) {
    flex: 1;
  }

  @media (${breakpoint.xlMin}) {
    padding-right: 0px;
    padding-left: 0px;
  }
`;

export const ArticleSocialAndContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px 0 0 0;
  @media (${breakpoint.mdMin}) {
    flex-direction: row;
  }
  @media (${breakpoint.xlMin}) {
    margin: 24px 0;
  }

  min-height: 500px;
`;

export const ArticleSocialContainer = styled.div`
  margin-bottom: 16px;
  margin-right: 24px;
  text-align: center;
  @media (${breakpoint.mdMin}) {
    margin-bottom: 0px;
    text-align: right;
    width: calc(((100% / 11) * 1) - 20px);
  }
  @media (${breakpoint.xlMin}) {
    margin-right: 48px;
    width: calc(((100% / 12) * 1));
  }
  @media (${breakpoint.xxlMin}) {
    padding-right: 50px;
    width: calc(((100% / 9) * 2));
  }
`;

export const ArticleHeroWrapper = styled.div`
  margin: 24px 16px 0px;

  & > .articleSeriesTop {
    margin-top: 24px;
  }

  @media (${breakpoint.mdMin}) {
    margin: 40px auto 0px;
    width: calc(((100% / 12) * 10) - 24px);
  }

  @media (${breakpoint.xlMin}) {
    margin-top: 48px;
    width: 100%;
  }

  @media (${breakpoint.xxlMin}) {
    margin: 48px 0px 0px auto;
    width: calc(((100% / 12) * 10) - 8px);
  }
`;

export const ArticleHeroTitle = styled(Heading)`
  margin: 8px 0px 0px 0px;
`;

export const ArticleHeroBylineWrapper = styled.div`
  font-family: ${font.graphikCond};
  font-size: 14px;
  letter-spacing: 1.2px;
  line-height: 21px;
  margin-right: 24px;
`;

export const ArticleHeroAuthorsContainer = styled.div`
  display: flex;
  font-size: 14px;
  font-weight: 300;
  letter-spacing: 1.2px;
  line-height: 21px;
  text-transform: uppercase;
`;

export const ArticleHeroDate = styled.div<{ theme: Theme }>`
  color: ${getColor('neutralDarkGray')};
  font-size: 14px;
  font-weight: 300;
  letter-spacing: 1.17px;
  line-height: 21px;
  margin-top: 4px;
  min-height: 21px;
`;

export const ArticleContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  padding: 0px 16px;

  @media (${breakpoint.xlMin}) {
    margin-top: 24px;
    flex-direction: row;
    padding: 0px
  }

  .source-caption {
    border: solid red;
  }
`;

export const ArticleImageAndContentContainer = styled.div`
  margin: 0 auto 0 0;
  width: 100%;

  @media (${breakpoint.mdMin}) {
    margin: 0 auto 48px 0;
  }

  @media (${breakpoint.mdMin}) {
    width: calc((100% / 12) * 11);
  }

  @media (${breakpoint.xlMin}) {
    margin: 0px 48px 0px 0px;
    width: calc(((100% / 12) * 9) - 12px);

    &.nativo-content__right-side {
      margin-right: 0;
    }
  }

  @media (${breakpoint.xxlMin}) {
    width: 100%;
  }

  .twitter-tweet {
    margin: auto !important;
  }

  iframe.instagram-media {
    margin: auto !important;
  }
`;

export const ArticleAdBlocker = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  font-family: ${font.graphikCond};
  padding: 0 40px;

  @media (${breakpoint.mdMin}) {
    padding: 0;
  }
`;

interface Subdomain {
  $subdomain?: string;
  $stickyTop?: number | null;
  $color?: string;
}

export const ArticleContentWrapper = styled.div<Subdomain & { theme: Theme }>`
  ${paragraphs};
  ${headers};
  ${images};
  ${blockQuotes};
  ${pullQuotes};
  ${longQuotes};
  ${videos};
  ${codeBlocks};
  ${tables};

  ${getBodyStyle('georgia', { default: 'medium' })};
  margin-top: 24px;

  a {
    color: ${getColor('textLink')};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &:active {
      color: ${getColor('textPrimary')};
      text-decoration: none;
    }
  }

  p {
    word-break: break-word;
  }

  div[class^="rawHtml-content"] {
    > p:first-of-type {
      margin-top: 0px;
    }
    h1, h2, h3, h4, h5, h6 {
      scroll-margin-top: ${({ $subdomain }) => handleScrollJump($subdomain)};
      @media (${breakpoint.mdMax}) {
        scroll-margin-top: ${({ $subdomain }) => handleScrollJumpMobile($subdomain)};
      }
    }
    iframe {
      aspect-ratio: 16 / 9;
    }

    @media (${breakpoint.mdMin}) {
      .paywall-selector {
        top: ${({ $stickyTop }) => ($stickyTop ? `${$stickyTop + 20}px` : '10%')};
      }
    }
  }

  & > .articleSeriesList {
    margin-top: 40px;
    scroll-margin-top: 150px;

    @media (min-width: 353px) {
      scroll-margin-top: 140px;
    }

    @media (min-width: 672px) {
      scroll-margin-top: 130px
    }

    a:hover {
      text-decoration: none;
    }
    
    h4, h5 {
      // overrides styling from headers.tsx
      margin-top: 0px !important;
      margin-bottom: 0px !important;
    }
  }

  .related-video-container {
    margin-bottom: 32px;
    h3 {
      font-weight: 600;
    }
    hr {
      margin: 0px 0px 16px;
    }
  }
    
  .ntv-video-wrapper {
      iframe {
          height: 100%;
          width: 100%;
      }
  }

  @media (${breakpoint.mdMin}) {
    width: calc((100% / 11) * 10);
    margin-left: auto;
    margin-right: 0;
  }

  @media (${breakpoint.xxlMin}) {
    width: calc(((100% / 9) * 7) - 8px);
    padding: 0 72px;
  }
`;
